import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import './Login.css';
import logo from '../assets/images/Guild.png';
import { getMultiFactorResolver, TotpMultiFactorGenerator } from 'firebase/auth';
import { auth } from '../firebase';

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    
    // MFA state
    const [mfaStep, setMfaStep] = useState(false);
    const [mfaResolver, setMfaResolver] = useState(null);
    const [mfaCode, setMfaCode] = useState('');

    const { signIn } = useAuth();
    const navigate = useNavigate();

    async function handleSubmit(e) {
        e.preventDefault();

        if (!email || !password) {
            setError('Please enter email and password');
            return;
        }

        try {
            setError('');
            setLoading(true);
            await signIn(email, password);
            navigate('/dashboard');
        } catch (err) {
            if (err.code === 'auth/multi-factor-auth-required') {
                const resolver = getMultiFactorResolver(auth, err);
                setMfaResolver(resolver);
                setMfaStep(true);
            } else {
                setError(err.message || 'Failed to sign in');
            }
        } finally {
            setLoading(false);
        }
    }

    async function handleMfaSubmit(e) {
        e.preventDefault();
        setError('');
        setLoading(true);

        if (mfaCode.length < 6) {
            setError('Please enter a valid 6-digit code.');
            setLoading(false);
            return;
        }

        try {
            const totpHint = mfaResolver.hints.find(
                hint => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID
            );
            if (!totpHint) throw new Error("TOTP not supported on this account.");

            const multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
                totpHint.uid,
                mfaCode
            );
            
            await mfaResolver.resolveSignIn(multiFactorAssertion);
            window.location.href = '/dashboard';
        } catch (err) {
            console.error("MFA Error:", err);
            setError(err.message || 'Verification failed. Please try again.');
            setLoading(false);
        }
    }

    return (
        <div className="login-container">
            <div className="login-card">
                <div className="login-header">
                    <div className="brand-logo">
                        <img src={logo} alt="AssistHealth" className="main-logo-img" />
                    </div>
                    <h2>Portal Login</h2>
                    <p>{mfaStep ? 'Two-Factor Authentication' : 'Secure access for Admins & Coordinators'}</p>
                </div>

                {!mfaStep ? (
                    <form onSubmit={handleSubmit} className="login-form">
                        {error && <div className="error-message">{error}</div>}

                        <div className="form-group">
                            <label htmlFor="email">Email</label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="your@email.com"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="password">Password</label>
                            <input
                                type="password"
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                required
                            />
                        </div>

                        <button type="submit" disabled={loading} className="login-button">
                            {loading ? 'Signing in...' : 'Sign In'}
                        </button>
                    </form>
                ) : (
                    <form onSubmit={handleMfaSubmit} className="login-form">
                        {error && <div className="error-message">{error}</div>}
                        
                        <p style={{ textAlign: 'center', marginBottom: '20px', color: '#666' }}>
                            Open your Authenticator app and enter the 6-digit code to continue.
                        </p>

                        <div className="form-group">
                            <input
                                type="text"
                                maxLength={6}
                                autoFocus
                                style={{ textAlign: 'center', fontSize: '24px', letterSpacing: '0.2em' }}
                                value={mfaCode}
                                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                                placeholder="000000"
                                required
                            />
                        </div>

                        <button type="submit" disabled={loading || mfaCode.length !== 6} className="login-button">
                            {loading ? 'Verifying...' : 'Verify Identity'}
                        </button>
                        
                        <button 
                            type="button" 
                            onClick={() => window.location.reload()}
                            className="login-button"
                            style={{ backgroundColor: 'transparent', color: '#666', marginTop: '10px', boxShadow: 'none' }}
                        >
                            Cancel & Log out
                        </button>
                    </form>
                )}

                <div className="login-footer">
                    <p>Health. Assured. Always.</p>
                </div>
            </div>
        </div>
    );
}
